
/**
 * Utility to handle file downloads with an optional filename prompt.
 * While we can't always force the browser's "Save As" location dialog due to iframe/browser restrictions,
 * we can provide a prompt to confirm or change the filename before downloading.
 */
export async function downloadFile(blob: Blob, suggestedName: string) {
  // 1. Try modern File System Access API (often blocked in iframes)
  if ('showSaveFilePicker' in window) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName,
        types: [{
          description: blob.type.includes('pdf') ? 'PDF Document' : 'CSV Spreadsheet',
          accept: { [blob.type]: [blob.type.includes('pdf') ? '.pdf' : '.csv'] },
        }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') return false;
      // If it fails due to security/iframe, we fallback
      console.warn('File System Access API failed or rejected:', err.message);
    }
  }

  // 2. Fallback: Prompt for filename if desired, then use the standard anchor download
  let finalName = suggestedName;
  const userRequestedName = window.prompt("Enter filename to save as:", suggestedName);
  
  if (userRequestedName === null) return false; // User cancelled
  if (userRequestedName.trim() !== "") {
    finalName = userRequestedName;
    // Append extension if missing
    const ext = suggestedName.split('.').pop();
    if (ext && !finalName.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
      finalName += `.${ext}`;
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = finalName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}
